// Based on material-ui TextField
// https://github.com/mui-org/material-ui/tree/master/packages/material-ui/src/TextField

import * as React from 'react';
import Input from '@material-ui/core/Input';
import FilledInput from '@material-ui/core/FilledInput';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl, {useFormControl} from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MediumEditor from './MediumEditor';
import {makeStyles} from '@material-ui/styles';
import {Theme} from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        paddingBottom: theme.spacing(2),
        fontFamily: 'Nunito',
        fontSize: 18,
        letterSpacing: 30 / 1000,

        '& p': {
            paddingBottom: theme.spacing(2),
        },
        '& ul': {
            //color: theme.palette.black,
            listStyle: 'none',
            paddingBottom: theme.spacing(2),

            '& li': {
                paddingBottom: theme.spacing(2),
                '&::before': {
                    content: '"o"',
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    fontSize: 14,
                    marginRight: theme.spacing(1),
                }
            },
        },
        '& h1': {
            paddingBottom: theme.spacing(2),
        },
        '& h2': {
            paddingBottom: theme.spacing(2),
        },
        '& h3': {
            paddingBottom: theme.spacing(2),
        },
    },

}));

const variantComponent = {
    standard: Input,
    filled: FilledInput,
    outlined: OutlinedInput,
};

// Okay, this logic is way crazy... was needed to set the label appropriately
// when already filled.  For some reason it detects as filled, then not filled
// at load
const FilledHandler = (props: { value: string }) => {
    const {onFilled, filled} = useFormControl();

    React.useEffect(() => {
        if (!filled && onFilled && props.value && props.value.trim()) {
            //setTimeout(onFilled, 10);
            onFilled();
        }
    }, [props.value, filled]);
    return null;
};

/**
 * The `MediumEditorField` is a convenience wrapper for the most common cases (80%).
 * It cannot be all things to all people, otherwise the API would grow out of control.
 *
 * ## Advanced Configuration
 *
 * It's important to understand that the text field is a simple abstraction
 * on top of the following components:
 *
 * - [FormControl](/api/form-control/)
 * - [InputLabel](/api/input-label/)
 * - [FilledInput](/api/filled-input/)
 * - [OutlinedInput](/api/outlined-input/)
 * - [Input](/api/input/)
 * - [FormHelperText](/api/form-helper-text/)
 *
 * If you wish to alter the props applied to the `input` element, you can do so as follows:
 *
 * ```jsx
 * const inputProps = {
 *   step: 300,
 * };
 *
 * return <MediumEditorField id="time" type="time" inputProps={inputProps} />;
 * ```
 *
 * For advanced cases, please look at the source of MediumEditorField by clicking on the
 * "Edit this page" button above. Consider either:
 *
 * - using the upper case props for passing values directly to the components
 * - using the underlying components directly as shown in the demos
 */
const MediumEditorField = React.forwardRef((props: any, ref) => {
    const containerEl = React.useRef();
    const classes = useStyles();

    const {
        autoComplete,
        autoFocus = false,
        children,
        className,
        color = 'primary',
        defaultValue,
        disabled = false,
        error = false,
        FormHelperTextProps,
        fullWidth = false,
        helperText,
        hiddenLabel,
        id,
        InputLabelProps,
        inputProps,
        InputProps,
        inputRef,
        label,
        multiline = false,
        name,
        onBlur,
        onChange,
        onFocus,
        placeholder,
        required = false,
        rows,
        rowsMax,
        type,
        value,
        variant = 'standard',
        targetBlank = true,
        ...other
    } = props;

    React.useEffect(() => {
        // work around TS for now
        const defaultNode = {
            addEventListener: (a: any, b: any) => null,
            querySelector: (a: any) => ({focus: () => null})
        };

        const node = containerEl ? containerEl.current! : defaultNode;
        node.addEventListener('click', () => {
            setTimeout(() => node.querySelector('[contenteditable]').focus(), 10);
        });
    }, []);

    const InputMore: any = {};

    if (variant === 'outlined') {
        if (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined') {
            InputMore.notched = InputLabelProps.shrink;
        }
        if (label) {
            const displayRequired = (InputLabelProps && InputLabelProps.required) || required;
            InputMore.label = (
                <React.Fragment>
                    {label}
                    {displayRequired && '\u00a0*'}
                </React.Fragment>
            );
        }
    }

    const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
    const inputLabelId = label && id ? `${id}-label` : undefined;
    let InputComponent: any = variantComponent.standard;

    switch (variant) {
        case 'standard':
            InputComponent = variantComponent.standard;
            break;
        case 'filled':
            InputComponent = variantComponent.filled;
            break;
        case 'outlined':
            InputComponent = variantComponent.outlined;
            break;
    }

    const InputElement = (
        <InputComponent
            aria-describedby={helperTextId}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            fullWidth={fullWidth}
            multiline={multiline}
            name={name}
            rows={rows}
            rowsMax={rowsMax}
            type={type}
            disabled={disabled}
            value={(value || '').trim()}
            id={id}
            inputRef={inputRef}
            //onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            placeholder={placeholder}
            inputProps={{
                ...inputProps,
                options: {
                    placeholder: false,
                    toolbar: {
                        buttons: disabled ? [] : ['bold', 'italic', 'h1', 'h2', 'h3', 'anchor', 'unorderedlist'],
                    },
                    targetBlank,
                },
                className: classes.root,
                style: {
                    width: 'calc(100% + 28px)',
                    outline: '0px solid transparent',
                    margin: '-18.5px -14px',
                    padding: '18.5px 14px',
                    ...(inputProps && inputProps.style),
                },
                height: rows ? `calc(${rows * 1.25}em + 37px)` : null,
                //minHeight: '1em',
            }}
            {...InputMore}
            {...InputProps}
            inputComponent={MediumEditor}
        />
    );

    return (
        <FormControl
            className={className}
            disabled={disabled}
            error={error}
            fullWidth={fullWidth}
            hiddenLabel={hiddenLabel}
            ref={containerEl}
            required={required}
            color={color}
            variant={variant}
            {...other}>
            <FilledHandler value={value}/>

            {label && (
                <InputLabel htmlFor={id} style={{backgroundColor: 'white'}} id={inputLabelId} {...InputLabelProps}>
                    {label}
                </InputLabel>
            )}
            {InputElement}

            {helperText && (
                <FormHelperText id={helperTextId} {...FormHelperTextProps}>
                    {helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
});

export default React.memo(MediumEditorField);
